import React from 'react';
import { Box, IconButton, Tooltip, Paper } from '@mui/material';
import { Delete, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { FieldSelector } from './FieldSelector';
import type { LogisticsObjectScope } from '@/types/logisticsObject';
import { SortOrderSelector } from './SortOrderSelector';
import { SortOrder } from '@/api/bol';

export interface SortRuleProps {
  field: string;
  order: SortOrder;
  onChange: (field: string, order: SortOrder) => void;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  disabled?: boolean;
  showMoveButtons?: boolean;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  size?: 'small' | 'medium';
  logisticsObjectType?: LogisticsObjectScope;
}

export const SortRule: React.FC<SortRuleProps> = ({
  field,
  order,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  disabled = false,
  showMoveButtons = false,
  canMoveUp = false,
  canMoveDown = false,
  size = 'small',
  logisticsObjectType = 'all'
}) => {
  const handleFieldChange = (newField: string | null) => {
    if (newField) {
      onChange(newField, order);
    }
  };

  const handleOrderChange = (newOrder: SortOrder) => {
    onChange(field, newOrder);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 1.5, 
        border: 1, 
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
      }}
    >
      {/* First Row: Field Selector and Delete Button */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FieldSelector
            value={field}
            onChange={handleFieldChange}
            disabled={disabled}
            size={size}
            logisticsObjectType={logisticsObjectType}
          />
        </Box>

        {/* Remove Button */}
        <Tooltip title="Remove sort rule">
          <span>
            <IconButton 
              onClick={onRemove} 
              disabled={disabled}
              size={size}
              color="error"
            >
              <Delete fontSize={size} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Second Row: Sort Order and Move Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SortOrderSelector
            value={order}
            onChange={handleOrderChange}
            disabled={disabled}
          />
        </Box>

        {/* Move Up/Down Buttons aligned to the right */}
        {showMoveButtons && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Move up">
              <span>
                <IconButton 
                  onClick={onMoveUp} 
                  disabled={disabled || !canMoveUp}
                  size={size}
                >
                  <ArrowUpward fontSize={size} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Move down">
              <span>
                <IconButton 
                  onClick={onMoveDown} 
                  disabled={disabled || !canMoveDown}
                  size={size}
                >
                  <ArrowDownward fontSize={size} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Paper>
  );
};
